import React from "react";
import { useTranslation } from "react-i18next";
import { scopedAnchor, usePracticeScope } from "../../../../onboarding/practice/practiceScope";

interface DetailHeaderProps {
    onToggleMaximize: () => void;
    onDelete: () => void;
    onClose: () => void;
    // When set, the top-left slot becomes a back arrow (replacing the maximize
    // button) and the close icon is hidden. Used when the modal was opened as a
    // child of another (e.g. a CHOICE editor opened from a BYO dish): the user
    // wants a clear "return to parent" affordance, not a window-style close.
    onBack?: () => void;
}

const DetailHeader: React.FC<DetailHeaderProps> = ({ onToggleMaximize, onDelete, onClose, onBack }) => {
    const { t } = useTranslation();
    const practiceScope = usePracticeScope();

    return (
        <div className="hidden md:flex items-center justify-between px-5 py-4 border-b border-[#E8E8E9] dark:border-borderPrimary-dark">
            {onBack ? (
                <button
                    type="button"
                    className="flex h-10 w-10 items-center justify-center rounded-lg border border-[#E8E8E9] dark:border-borderPrimary-dark"
                    data-onboarding={scopedAnchor(practiceScope, "cost-detail-close")}
                    onClick={onBack}
                    title={t('common.back')}
                >
                    <img src="/assets/arrow-narrow-left.svg" alt="back" />
                </button>
            ) : (
                <button
                    type="button"
                    className="flex h-10 w-10 items-center justify-center rounded-lg border border-[#E8E8E9] dark:border-borderPrimary-dark"
                    onClick={onToggleMaximize}
                >
                    <img src="/assets/maximize.svg" alt="expand" />
                </button>
            )}

            <div className="flex items-center gap-2">
                <button
                    type="button"
                    onClick={onDelete}
                    data-onboarding={scopedAnchor(practiceScope, "cost-detail-delete")}
                    className="flex h-10 w-10 items-center justify-center rounded-lg border border-[#E8E8E9] dark:border-borderPrimary-dark hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors"
                    title={t('costPrice.detail.deleteConfirmTitle')}
                >
                    <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M2.5 5H4.16667H17.5" stroke="#F04438" strokeWidth="1.67" strokeLinecap="round" strokeLinejoin="round"/>
                        <path d="M6.66699 5V3.33333C6.66699 2.89131 6.84259 2.46738 7.15515 2.15482C7.46771 1.84226 7.89164 1.66667 8.33366 1.66667H11.667C12.109 1.66667 12.5329 1.84226 12.8455 2.15482C13.158 2.46738 13.3337 2.89131 13.3337 3.33333V5M15.8337 5V16.6667C15.8337 17.1087 15.6581 17.5326 15.3455 17.8452C15.033 18.1577 14.609 18.3333 14.167 18.3333H5.83366C5.39164 18.3333 4.96771 18.1577 4.65515 17.8452C4.34259 17.5326 4.16699 17.1087 4.16699 16.6667V5H15.8337Z" stroke="#F04438" strokeWidth="1.67" strokeLinecap="round" strokeLinejoin="round"/>
                        <path d="M8.33301 9.16667V14.1667" stroke="#F04438" strokeWidth="1.67" strokeLinecap="round" strokeLinejoin="round"/>
                        <path d="M11.667 9.16667V14.1667" stroke="#F04438" strokeWidth="1.67" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                </button>
                {!onBack && (
                    <button
                        type="button"
                        data-onboarding={scopedAnchor(practiceScope, "cost-detail-close")}
                        onClick={onClose}
                        className="flex h-10 w-10 items-center justify-center rounded-lg border border-[#E8E8E9] dark:border-borderPrimary-dark"
                    >
                        <img src="/assets/close-modal.svg" alt="close" />
                    </button>
                )}
            </div>
        </div>
    );
};

export default DetailHeader;
